import DashboardRoutesList from 'pages/merchant/dashboard/constants';
import React, { useMemo, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';
import { useAppSelector } from 'redux/merchant/hooks';

import { Button, FlexContainer, Heading, Icon } from '@limepayments/cosmic';

import PayoutSidebar from '../../partials/PayoutSidebar';
import { SettlementsQueryEdges, getFilterCount } from '../types';
// Import Static UIs
import ExportComponent from './ExportComponent';
import FilterComponent from './FilterComponent';
import PayoutBreakdownDataTable from './PayoutBreakdownDataTable';

export interface Props {
  payoutList: SettlementsQueryEdges;
  fetchListLoader: boolean;
  totalRows: number;
  handlePerRowsChange: (newPerPage: number, page: number) => void;
  handlePageChange: (page: number) => void;
  limit: number;
  toggleFilter: () => void;
  filterPopupOpen: boolean;
  activePage: number;
  handleSort: (selectedColumn: TableColumn<SettlementsQueryEdges[number]>, sortDirection: SortOrder) => void;
  isFilterRequest?: boolean;
  isErrorRequest?: boolean;
}

function PayoutBreakdownPage({
  payoutList,
  totalRows,
  limit,
  fetchListLoader,
  handlePageChange,
  handlePerRowsChange,
  toggleFilter,
  filterPopupOpen,
  activePage,
  handleSort,
  isFilterRequest,
  isErrorRequest,
}: Props) {
  const [isExportOpened, setIsExportOpened] = useState(false);

  const { payoutBreakdownFilterObj } = useAppSelector((state) => ({
    payoutBreakdownFilterObj: state.payout.payoutBreakdownFilterObj,
  }));

  function toggleExport() {
    setIsExportOpened((wasExportOpened) => !wasExportOpened);
  }

  const filterCount = useMemo(() => getFilterCount(payoutBreakdownFilterObj), [payoutBreakdownFilterObj]);

  return (
    <div className="content-wrap-inner lp-full m-0">
      <div className="lp-w-full lp-flex">
        <PayoutSidebar activeTab={DashboardRoutesList.PAYOUT_BREAKDOWN_ROUTE} />

        <div className="payment-content-area">
          <div className="lp-w-full lp-flex lp-justify-between lp-align-start sm:flex-col mb-16">
            <Heading alignment="left" tagName="h2" variant="sm">
              Payout breakdown
            </Heading>
            <div className="merchant-controls sm-mt-16 sm:lp-full sm:pr-0">
              <FlexContainer classNames="lp-flex m-0">
                <div className="lp-flex order-2 position-relative m-0 mb-0">
                  <Button
                    className="lp-w-full"
                    size="small"
                    variant="reversed"
                    onClick={() => toggleFilter()}
                    disabled={payoutList.length < 1 && filterCount === 0}
                  >
                    <Icon name="Filter" className="ml-0 mr-8" />
                    Filter ({filterCount})
                  </Button>

                  <Button disabled className="lp-w-full" size="small" variant="reversed" onClick={toggleExport}>
                    <Icon name="ExternalLink" className="ml-0 mr-8" />
                    Export
                  </Button>

                  {/* Filter Dropdown */}
                  {filterPopupOpen && (
                    <FilterComponent modalToggler={toggleFilter} handlePageChange={handlePageChange} />
                  )}

                  {/* Export Dropdown */}
                  {isExportOpened && (
                    <div className="filter-dropdown w-656">
                      <ExportComponent onCloseExportClick={toggleExport} />
                    </div>
                  )}
                </div>
              </FlexContainer>
            </div>
          </div>

          <PayoutBreakdownDataTable
            payoutList={payoutList}
            fetchListLoader={fetchListLoader}
            totalRows={totalRows}
            handlePerRowsChange={handlePerRowsChange}
            handlePageChange={handlePageChange}
            limit={limit}
            activePage={activePage}
            handleSort={handleSort}
            isFilterRequest={isFilterRequest || filterCount > 0}
            isErrorRequest={isErrorRequest}
            toggleFilter={toggleFilter}
          />
        </div>
      </div>
    </div>
  );
}

export default PayoutBreakdownPage;
